import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Download, HardDrive, Cloud, ChevronDown, X } from "lucide-react";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

function useOutside(ref, onClose) {
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [ref, onClose]);
}

async function fetchBackupBlob() {
  const r = await fetch(`${API}/backup/export.xlsx`, { credentials: "include" });
  if (!r.ok) throw new Error("Yedek indirilemedi");
  const cd = r.headers.get("Content-Disposition") || "";
  const m = cd.match(/filename="([^"]+)"/);
  const filename = m ? m[1] : `misland-yedek-${new Date().toISOString().slice(0,10)}.xlsx`;
  const blob = await r.blob();
  return { blob, filename };
}

async function loadGsi() {
  if (window.google?.accounts?.oauth2) return;
  await new Promise((resolve, reject) => {
    if (document.getElementById("gsi-client")) return resolve();
    const s = document.createElement("script");
    s.id = "gsi-client";
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true; s.defer = true;
    s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });
  // Give GSI a beat to attach
  await new Promise(r => setTimeout(r, 50));
}

async function uploadToDrive(blob, filename, token) {
  const metadata = { name: filename, mimeType: blob.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" };
  const form = new FormData();
  form.append("metadata", new Blob([JSON.stringify(metadata)], { type: "application/json" }));
  form.append("file", blob);
  const r = await fetch("https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,webViewLink", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (!r.ok) {
    const err = await r.text();
    throw new Error(err || "Drive yüklemesi başarısız");
  }
  return r.json();
}

export default function BackupButton() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showDriveInfo, setShowDriveInfo] = useState(false);
  const ref = useRef(null);
  useOutside(ref, () => setOpen(false));

  const downloadLocal = async () => {
    setBusy(true);
    try {
      const { blob, filename } = await fetchBackupBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Yedek bilgisayarınıza indirildi");
    } catch (e) {
      toast.error(e.message || "Yedek alınamadı");
    } finally { setBusy(false); setOpen(false); }
  };

  const uploadDrive = async () => {
    if (!GOOGLE_CLIENT_ID) {
      setShowDriveInfo(true);
      setOpen(false);
      return;
    }
    setBusy(true);
    try {
      await loadGsi();
      const { blob, filename } = await fetchBackupBlob();
      await new Promise((resolve, reject) => {
        const tc = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: "https://www.googleapis.com/auth/drive.file",
          callback: async (resp) => {
            if (resp.error) return reject(new Error(resp.error));
            try {
              const created = await uploadToDrive(blob, filename, resp.access_token);
              toast.success(`Drive'a yüklendi: ${created.name}`);
              if (created.webViewLink) window.open(created.webViewLink, "_blank");
              resolve();
            } catch (e) { reject(e); }
          },
        });
        tc.requestAccessToken({ prompt: "" });
      });
    } catch (e) {
      toast.error(e.message || "Drive yüklemesi başarısız");
    } finally { setBusy(false); setOpen(false); }
  };

  return (
    <>
      <div className="relative" ref={ref}>
        <button
          data-testid="backup-button"
          disabled={busy}
          onClick={() => setOpen(o => !o)}
          className="inline-flex items-center gap-2 rounded-md bg-slate-900 text-white font-semibold text-sm px-4 py-2.5 hover:bg-slate-800 disabled:opacity-60"
        >
          <Download size={15} /> {busy ? "İşleniyor..." : "Yedek Al"} <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="absolute right-0 mt-2 w-72 crisp-card p-1.5 z-30 shadow-lg" data-testid="backup-menu">
            <button
              data-testid="backup-local"
              onClick={downloadLocal}
              className="w-full flex items-start gap-3 rounded-md px-3 py-2.5 text-left hover:bg-slate-50">
              <HardDrive size={18} className="text-slate-500 mt-0.5" />
              <div>
                <div className="text-sm font-bold text-slate-900">Bilgisayara İndir</div>
                <div className="text-[11px] text-slate-500">Tarayıcının indirilenler klasörüne .xlsx olarak kaydeder.</div>
              </div>
            </button>
            <button
              data-testid="backup-drive"
              onClick={uploadDrive}
              className="w-full flex items-start gap-3 rounded-md px-3 py-2.5 text-left hover:bg-slate-50">
              <Cloud size={18} className="text-slate-500 mt-0.5" />
              <div>
                <div className="text-sm font-bold text-slate-900">Google Drive'a Yükle</div>
                <div className="text-[11px] text-slate-500">Google hesabınıza yeni bir Drive dosyası olarak yükler.</div>
              </div>
            </button>
          </div>
        )}
      </div>

      {showDriveInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="drive-info-modal">
          <div className="crisp-card p-6 w-full max-w-lg">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-lg font-bold text-slate-900">Google Drive Kurulumu</h3>
              <button onClick={() => setShowDriveInfo(false)} className="text-slate-500 hover:text-slate-900"><X size={18} /></button>
            </div>
            <div className="text-sm text-slate-700 space-y-3">
              <p>Drive'a otomatik yükleme için Google Cloud üzerinden ücretsiz bir OAuth istemci kimliği oluşturmanız gerekiyor. Kısa adımlar:</p>
              <ol className="list-decimal ml-5 space-y-1 text-sm">
                <li><a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="text-slate-900 underline underline-offset-2">Google Cloud → APIs & Services → Credentials</a> sayfasını açın.</li>
                <li><b>OAuth client ID → Web application</b> oluşturun.</li>
                <li>Authorized JavaScript origins: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{window.location.origin}</code></li>
                <li><b>Google Drive API</b>'yi etkinleştirin.</li>
                <li>Aldığınız <b>Client ID</b>'yi bize iletin — biz de <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">REACT_APP_GOOGLE_CLIENT_ID</code> olarak ekleyelim.</li>
              </ol>
              <p className="text-xs text-slate-500">Client ID gelene kadar <b>Bilgisayara İndir</b> ile yedek alabilir, dosyayı Drive'a manuel olarak sürükleyip bırakabilirsiniz.</p>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShowDriveInfo(false)} className="px-4 py-2 rounded-md border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50">Kapat</button>
              <button onClick={() => { setShowDriveInfo(false); downloadLocal(); }} className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">Bilgisayara İndir</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
